import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box, TextField, Button, Typography, Alert, CircularProgress,
    Card, CardContent, Divider, MenuItem, FormControl, InputLabel,
    Select, ThemeProvider, createTheme, LinearProgress, InputAdornment, IconButton
} from '@mui/material';
import {
    FitnessCenter, PersonAdd, Email, Lock, CheckCircle,
    Visibility, VisibilityOff
} from '@mui/icons-material';
import SEO from '../components/SEO'; // ✅ ДОДАДЕНО

const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: '#4dabf7' },
        background: { default: '#fafafa', paper: '#ffffff' },
    }
});

// ✅ ИСТО правило како во backend: min 6, max 24, 1 голема буква, 1 бројка
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[0-9]).{6,24}$/;

const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*()]/.test(password)) score++;

    if (score <= 1) return { score: 25, label: '🔴 Слаба лозинка', color: '#f44336' };
    if (score === 2) return { score: 50, label: '🟠 Средна лозинка', color: '#ff9800' };
    if (score === 3) return { score: 75, label: '🟡 Добра лозинка', color: '#ffc107' };
    return { score: 100, label: '🟢 Одлична лозинка', color: '#4CAF50' };
};

const Register = () => {
    const [formData, setFormData] = useState({
        email: '', password: '', confirmPassword: '',
        firstName: '', lastName: '', gender: ''
    });
    const [errors, setErrors] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const navigate = useNavigate();
    const passwordStrength = getPasswordStrength(formData.password);

    const validateField = (name, value) => {
        let err = '';
        if (name === 'firstName' || name === 'lastName') {
            if (!value.trim()) err = 'Задолжително поле';
        } else if (name === 'email') {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) err = 'Невалиден email';
        } else if (name === 'password') {
            if (!PASSWORD_REGEX.test(value)) {
                err = 'Лозинката треба да содржи минимум 6 карактери, 1 голема буква и 1 бројка';
            }
        } else if (name === 'confirmPassword') {
            if (value !== formData.password) err = 'Лозинките не се совпаѓаат';
        }
        return err;
    };

    const validateAll = () => {
        const newErrors = {
            firstName: validateField('firstName', formData.firstName),
            lastName: validateField('lastName', formData.lastName),
            email: validateField('email', formData.email),
            password: validateField('password', formData.password),
            confirmPassword: validateField('confirmPassword', formData.confirmPassword),
        };
        setErrors(newErrors);
        return Object.values(newErrors).every((e) => !e);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateAll()) {
            setError('Провери ги полињата означени со грешка');
            return;
        }

        setLoading(true);
        try {
            const response = await authAPI.register({ ...formData, role: 'USER' });
            setSuccess(true);
            setSuccessMessage(response.message);
        } catch (err) {
            setError(err.response?.data?.message || 'Грешка при регистрација');
        } finally {
            setLoading(false);
        }
    };

    const primaryBtnSx = {
        py: 1.8, borderRadius: '14px', fontWeight: '800',
        fontSize: '1.05rem', textTransform: 'none',
        background: 'linear-gradient(45deg, #4dabf7 30%, #a5d8ff 90%)',
        boxShadow: '0 4px 15px rgba(77, 171, 247, 0.4)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'linear-gradient(45deg, #339af0 30%, #74c0fc 90%)',
            transform: 'translateY(-2px)',
        }
    };

    const loginLinkBtnSx = {
        py: 1.5, borderRadius: '14px', fontWeight: '700',
        textTransform: 'none',
        background: 'linear-gradient(45deg, #ff7eb9 30%, #ff4a97 90%)',
        color: '#ffffff', boxShadow: '0 4px 12px rgba(255, 126, 185, 0.3)',
        '&:hover': {
            background: 'linear-gradient(45deg, #ff4a97 30%, #e63985 90%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 15px rgba(255, 126, 185, 0.4)',
        }
    };

    if (success) {
        return (
            <ThemeProvider theme={lightTheme}>
                <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                    <Card sx={{ maxWidth: 500, width: '100%', borderRadius: '24px', textAlign: 'center', p: 4, border: '2px solid #4CAF50' }}>
                        <CheckCircle sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
                        <Typography variant="h4" sx={{ fontWeight: '900', mb: 2 }}>Успешно!</Typography>
                        <Alert severity="success" sx={{ borderRadius: '12px', mb: 4 }}>{successMessage}</Alert>
                        <Button fullWidth variant="contained" onClick={() => navigate('/login')} sx={loginLinkBtnSx}>
                            Оди кон најава
                        </Button>
                    </Card>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={lightTheme}>

            {/* ✅ SEO */}
            <SEO
                title="Регистрација - BodyLab by Anamarija"
                description="Создај бесплатен профил и започни ја твојата фитнес трансформација со BodyLab by Anamarija. Персонализирани програми и диети за жени."
                url="/register"
            />

            <Box sx={{
                py: 6, minHeight: '100vh', display: 'flex',
                justifyContent: 'center', alignItems: 'center', bgcolor: '#fafafa'
            }}>
                <Box sx={{ width: '100%', maxWidth: 550, px: 2 }}>
                    <Card sx={{
                        borderRadius: '24px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.05)',
                        border: '1px solid #e0e0e0'
                    }}>
                        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <FitnessCenter sx={{ fontSize: 50, color: '#4dabf7', mb: 1 }} />

                                {/* ✅ H1 */}
                                <Typography variant="h1" sx={{
                                    fontWeight: '900', color: '#d63384',
                                    fontSize: { xs: '1.6rem', md: '2rem' }
                                }}>
                                    Креирај профил
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#868e96' }}>
                                    Започни ја твојата трансформација денес
                                </Typography>
                            </Box>

                            {error && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <TextField fullWidth label="Име" name="firstName"
                                               onChange={handleChange} onBlur={handleBlur}
                                               error={!!errors.firstName}
                                               helperText={errors.firstName} />
                                    <TextField fullWidth label="Презиме" name="lastName"
                                               onChange={handleChange} onBlur={handleBlur}
                                               error={!!errors.lastName}
                                               helperText={errors.lastName} />
                                </Box>

                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Пол</InputLabel>
                                    <Select name="gender" value={formData.gender}
                                            onChange={handleChange} label="Пол">
                                        <MenuItem value="Male">👨 Машки</MenuItem>
                                        <MenuItem value="Female">👩 Женски</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth label="Email адреса" name="email"
                                    onChange={handleChange} onBlur={handleBlur}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment>,
                                        sx: { borderRadius: '12px' }
                                    }}
                                />

                                <TextField
                                    fullWidth label="Лозинка" name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    onChange={handleChange} onBlur={handleBlur}
                                    error={!!errors.password}
                                    helperText={
                                        errors.password
                                            ? errors.password
                                            : 'Лозинката треба да содржи минимум 6 карактери, 1 голема буква и 1 бројка'
                                    }
                                    sx={{ mb: 1 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: '12px' }
                                    }}
                                />

                                {formData.password && (
                                    <Box sx={{ mb: 2 }}>
                                        <LinearProgress variant="determinate"
                                                        value={passwordStrength.score}
                                                        sx={{
                                                            height: 6, borderRadius: 3, bgcolor: '#e9ecef',
                                                            '& .MuiLinearProgress-bar': { bgcolor: passwordStrength.color }
                                                        }} />
                                        <Typography variant="caption" sx={{ color: passwordStrength.color, fontWeight: '700' }}>
                                            {passwordStrength.label}
                                        </Typography>
                                    </Box>
                                )}

                                <TextField
                                    fullWidth label="Потврди лозинка" name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    onChange={handleChange} onBlur={handleBlur}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword}
                                    sx={{ mb: 4 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: '12px' }
                                    }}
                                />

                                <Button type="submit" fullWidth variant="contained"
                                        disabled={loading} sx={primaryBtnSx}>
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Креирај профил'}
                                </Button>
                            </form>

                            <Divider sx={{ my: 4 }}>Имаш профил?</Divider>

                            <Button component={Link} to="/login" fullWidth
                                    variant="contained" sx={loginLinkBtnSx}>
                                Најави се тука
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default Register;